import { useEffect, useState } from "react";
import type { MaterialsListData } from "@workspace/api-zod/materials-list";
import { materialsConfirmationFingerprint } from "./materials-order-data";

const HANDOFF = "proper-tool:materials-confirmation-handoff";
const storageKey = (quoteId: string) => `proper-tool:materials-confirmed:${quoteId}`;

/** Only a successful draft save can transfer acknowledgement to a quote ID. */
export function commitDraftMaterialsConfirmation(quoteId: string, data: MaterialsListData) {
  try {
    const raw = sessionStorage.getItem(HANDOFF);
    if (!raw) return;
    const handoff = JSON.parse(raw);
    if (handoff.fingerprint === materialsConfirmationFingerprint(data) && handoff.expires > Date.now()) {
      localStorage.setItem(storageKey(quoteId), handoff.fingerprint);
    }
    sessionStorage.removeItem(HANDOFF);
  } catch {
    // Browser-local acknowledgement must never interfere with saving a quote.
  }
}

/** Browser-local acknowledgement, never a server approval or customer acceptance.
 * A short-lived, single-use handoff preserves it when a draft is first saved. */
export function useMaterialsConfirmation(fingerprint: string, quoteId?: string) {
  const scope = quoteId ?? "draft";
  const [acknowledgement, setAcknowledgement] = useState<{ scope: string; fingerprint: string } | null>(null);
  useEffect(() => {
    if (!quoteId) return;
    try {
      const stored = localStorage.getItem(storageKey(quoteId));
      if (stored === fingerprint) setAcknowledgement({ scope: quoteId, fingerprint });
    } catch {
      // Storage can be unavailable in private browsing; this editor still works.
    }
  }, [quoteId, fingerprint]);
  const confirm = () => {
    setAcknowledgement({ scope, fingerprint });
    try {
      if (quoteId) localStorage.setItem(storageKey(quoteId), fingerprint);
      else sessionStorage.setItem(HANDOFF, JSON.stringify({ fingerprint, expires: Date.now() + 120_000 }));
    } catch {
      // Acknowledgement remains valid for this editor session.
    }
  };
  return { confirmed: acknowledgement?.scope === scope && acknowledgement.fingerprint === fingerprint, confirm };
}
